import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ExternalLink, Github, Code2, Sparkles, Rocket } from 'lucide-react'
import { projects } from '../data/projects'
import VideoPlayer from '../components/VideoPlayer'

const Projects = () => {
  const [filter, setFilter] = useState('all')

  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'game', label: 'Game Development' },
    { id: 'ai', label: 'AI/ML' },
    { id: 'web3', label: 'Web3' },
    { id: 'web', label: 'Web' },
  ]

  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter(p => p.category === filter)

  return (
    <div className="min-h-screen pt-16">
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gradient">
              Projects
            </h1>
            <p className="text-xl text-base-content/70 max-w-2xl mx-auto">
              A collection of projects showcasing my skills and passion for
              creating innovative solutions
            </p>
          </motion.div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setFilter(category.id)}
                className={`btn ${
                  filter === category.id ? 'btn-primary' : 'btn-outline'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={filter}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className={`card glass overflow-hidden ${
                    project.featured ? 'md:col-span-2 lg:col-span-2' : ''
                  }`}
                >
                  <div className="relative h-48 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center overflow-hidden">
                    {project.featured && (
                      <div className="absolute top-4 right-4 z-10 badge badge-primary">
                        Featured
                      </div>
                    )}
                    {project.image && project.image.endsWith('.mp4') ? (
                      <VideoPlayer
                        src={project.image}
                        className="absolute inset-0"
                      />
                    ) : project.image && project.image !== '/api/placeholder/600/400' ? (
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextSibling.style.display = 'flex'
                        }}
                      />
                    ) : null}
                    <div className="absolute inset-0 flex items-center justify-center" style={{ display: project.image && !project.image.endsWith('.mp4') && project.image !== '/api/placeholder/600/400' ? 'none' : 'flex' }}>
                      <Code2 className="w-16 h-16 text-primary/50" />
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="flex items-start justify-between mb-2">
                      <h2 className="card-title">{project.title}</h2>
                      {project.year && (
                        <span className="badge badge-primary badge-sm">{project.year}</span>
                      )}
                    </div>
                    {project.company && (
                      <p className="text-sm text-primary mb-2 font-medium">{project.company}</p>
                    )}
                    <p className="text-base-content/70">{project.description}</p>
                    {project.highlights && project.highlights.length > 0 && (
                      <div className="mt-4 mb-4">
                        <p className="text-sm font-semibold mb-2 text-base-content/80">Key Highlights:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-base-content/70">
                          {project.highlights.map((highlight, idx) => (
                            <li key={idx}>{highlight}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-4">
                      {project.tags.map((tag) => (
                        <span
                          key={tag}
                          className="badge badge-outline badge-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="card-actions justify-end mt-4">
                      {project.link !== '#' && (
                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-primary"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Live Demo
                        </a>
                      )}
                      {project.github !== '#' && (
                        <a
                          href={project.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline"
                        >
                          <Github className="w-4 h-4 mr-2" />
                          Code
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
    </div>
  )
}

export default Projects
